/**
 * Browser-safe JS mirror of the canonical waste-stream amount rule.
 *
 * The SQL half of the rule lives in ./effectiveAmount.ts, which imports the
 * Prisma client and therefore must never be imported from Oak — the generated
 * client references `process` and crashes the browser at module init. Keep
 * this module dependency-free so frontend code can share the rule.
 */

export type EffectiveAmountLine = {
  eManifestAmount: number | null;
  vendorAmount: number | null;
  /**
   * Whether the line's source document has been human-reviewed. Derive this
   * from the live DocumentMetadata state (needsReview = false AND reviewedAt
   * set), matching the SQL fragment's EXISTS — NOT from the denormalized
   * WasteLineUnified.documentReviewed column, which is refreshed by a queued
   * process-manifests run and can lag a just-completed review.
   */
  documentReviewed?: boolean;
  documentAmount?: number | null;
};

/**
 * The canonical per-line effective amount (see effectiveAmount.ts module doc):
 * eManifest → vendor → reviewed document; zero = not reported.
 */
export function effectiveLineAmount(line: EffectiveAmountLine): number {
  if (line.eManifestAmount) return line.eManifestAmount;
  if (line.vendorAmount) return line.vendorAmount;
  if (line.documentReviewed && line.documentAmount) return line.documentAmount;
  return 0;
}
